// A draft row's messages, in the words the report and the movement dialog both
// use (spec 054 FR-015, FR-020): under the medicine in the table, and on the
// field they concern when the row is opened in the dialog. The field comes from
// the message's own `field` key, never from matching its text (the wireframe's
// regex over the message is the defect not copied).
//
// An imported cell core could not read (research R11) is kept with the file's
// own text in `sourceIssues`, and that text follows the sentence, so the
// operator sees what the file held.
import type { useTranslate } from '@tolgee/react';
import { formatExpiry } from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import type { DraftRow, DraftRowSourceIssue, DraftRowValidationError } from './api';
import { MOVEMENT_REASONS, reasonLabelKey, type MovementField } from './movement-form';

type TFn = ReturnType<typeof useTranslate>['t'];

// Core's row field names (DraftRowValidationError.field) against the dialog's.
const MOVEMENT_FIELD_OF: Readonly<Record<string, MovementField>> = {
  medicineCode: 'medicine',
  lot: 'lot',
  lotExpiry: 'lotExpiry',
  currentQuantity: 'quantity',
  reason: 'reason',
  comment: 'comment',
};

/** The dialog field a row message belongs on; the medicine panel for the rest. */
export function movementFieldOf(field: string): MovementField {
  return MOVEMENT_FIELD_OF[field] ?? 'medicine';
}

function sourceOf(row: DraftRow, field: string): DraftRowSourceIssue | undefined {
  return row.sourceIssues?.find((issue) => issue.field === field);
}

// A code the one code-to-copy table gives to another screen, read by the row
// field it sits on. EXPIRY_FORMAT is the correction decision's "Data aprobată nu
// are un format valid." there; on a row's expiry it is the dialog's own rule,
// "Scrie luna și anul ca LL/AAAA" (contracts/ui-flow.md; spec 054 T098).
// MEDICINE_REQUIRED and REASON_REQUIRED are the availability query's there; on a
// row they are the dialog's own rules, and CORRECTION_WINDOW_EXPIRED is the
// dialog's window notice (docs/shape-ops-row-validation.md §Decision 4).
const ROW_FIELD_KEYS: Readonly<Record<string, string>> = {
  'lotExpiry:EXPIRY_FORMAT': 'stock_reporting.movement.error.expiry_format',
  'medicineCode:MEDICINE_REQUIRED': 'stock_reporting.movement.error.medicine_required',
  'reason:REASON_REQUIRED': 'stock_reporting.movement.error.reason_required',
  'correctionOf:CORRECTION_WINDOW_EXPIRED': 'stock_reporting.dialog.correction.window.title',
};

const UNKNOWN_CODE_KEY = 'stock_reporting.movement.error.unknown_code';

function rowIssueKey(field: string, code: string, fallbackKey: string): string {
  return (
    ROW_FIELD_KEYS[`${field}:${code}`] ?? apiErrorKey({ status: 0, code, message: '' }, fallbackKey)
  );
}

// The sentence, one colon, then the file's text. A sentence that ends in a stop
// gives it up, so the line never reads ".: „…”" (T098).
function withSourceValue(t: TFn, message: string, raw: string): string {
  return t('stock_reporting.row.source_value', { message: message.replace(/\.\s*$/, ''), raw });
}

/**
 * The precision to show a draft row's own expiry with (BUG-1-029). The row carries
 * the date the operator gave; when that is the date the lot is already known by,
 * the lot's stored precision applies. A date for a lot nobody has reported is shown
 * in full: it is stored as a day when the report is confirmed.
 */
export function rowExpiryPrecision(row: DraftRow): string | undefined {
  if (!row.knownExpiry || row.knownExpiry !== row.lotExpiry) return undefined;
  return row.knownExpiryPrecision ?? undefined;
}

// What a sentence may name of the row: EXPIRY_MISMATCH names the lot's own
// expiry, the one the server read for it (docs/shape-ops-existing-lot-alerts.md
// §Decision 3), and COMMENT_REQUIRED the row's reason, as the dialog's sentence
// does. Every other sentence names none of these.
function rowParams(t: TFn, row: DraftRow): Record<string, string> {
  const reason = MOVEMENT_REASONS.find((code) => code === row.reason);
  return {
    expiry: formatExpiry(row.knownExpiry, row.knownExpiryPrecision),
    reason: reason ? t(reasonLabelKey(reason)) : (row.reason ?? ''),
  };
}

/** One validation message, in the one code-to-copy table's words where it has them. */
export function rowIssueMessage(t: TFn, issue: DraftRowValidationError, row: DraftRow): string {
  const key = rowIssueKey(issue.field, issue.code, '');
  // No key: a sentence in the reader's language naming the code, never the
  // server's own text (owner decision Q-1-03, docs/shape-ops-api-errors.md §4).
  if (!key) return t(UNKNOWN_CODE_KEY, { code: issue.code });
  const source = sourceOf(row, issue.field);
  if (!source) return t(key, rowParams(t, row));
  return withSourceValue(t, t(key, rowParams(t, row)), source.raw);
}

/**
 * The row's messages by dialog field, the first per field. Source issues a save
 * has not folded into `validationErrors` yet (a row the browser still holds)
 * are read directly.
 */
export function rowFieldMessages(t: TFn, row: DraftRow): Partial<Record<MovementField, string>> {
  const messages: Partial<Record<MovementField, string>> = {};
  for (const issue of row.validationErrors) {
    const field = movementFieldOf(issue.field);
    messages[field] ??= rowIssueMessage(t, issue, row);
  }
  for (const source of row.sourceIssues ?? []) {
    const field = movementFieldOf(source.field);
    if (messages[field] !== undefined) continue;
    const key = rowIssueKey(
      source.field,
      source.code,
      'stock_reporting.movement.error.source_value_invalid',
    );
    messages[field] = withSourceValue(t, t(key), source.raw);
  }
  return messages;
}
